/**
 * The mentor's view of a tutor: their students, how much each has asked about
 * this module, and any student's conversation, read only. Shared by the session
 * page (the batch tutor) and the reading page (the book tutor).
 *
 * loadStudents() → [{id, name, exchanges, last_at}]
 * loadChat(userId, before) → {messages, has_more}
 * loadThreads(userId) → the student's side conversations (tabs) [{id, parent, title, quote, exchanges, last_at}], optional
 * loadThreadChat(userId, threadId, before) → {messages, has_more}
 */

import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import CallSplitIcon from "@mui/icons-material/CallSplit";

import { errorMessage } from "../ContentBank/contentBankApi";
import { FONT } from "../Sessions/sessionParts";
import { Message } from "./TutorChat";
import { flatten } from "./TutorTabs";

const ago = (epoch) => {
  if (!epoch) return "never";
  const s = Date.now() / 1000 - epoch;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

export default function TutorReview({ loadStudents, loadChat, loadThreads, loadThreadChat, onSource, isClickable }) {
  const [students, setStudents] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);           // the student being read
  const [page, setPage] = useState({ messages: [], has_more: false });
  const [busy, setBusy] = useState(false);
  const [threads, setThreads] = useState([]);       // the open student's side conversations
  const [thread, setThread] = useState(null);       // the one being read, else the main conversation
  const want = useRef(null);                        // the student a response must be for

  const loaders = useRef({ loadStudents, loadChat, loadThreads, loadThreadChat });
  loaders.current = { loadStudents, loadChat, loadThreads, loadThreadChat };

  useEffect(() => {
    loaders.current.loadStudents().then(setStudents).catch((e) => setError(errorMessage(e, "Couldn't load the students")));
  }, []);

  const read = async (student, before = null, inThread = null) => {
    const token = `${student.id}:${inThread ? inThread.id : "main"}`;    // the response must be for this view
    want.current = token;
    setBusy(true);
    setError("");
    if (before === null) {
      setOpen(student);
      setThread(inThread);
      setPage({ messages: [], has_more: false });
      if (inThread === null && loaders.current.loadThreads) {
        setThreads([]);
        Promise.resolve().then(() => loaders.current.loadThreads(student.id))
          .then((rows) => { if (want.current === token) setThreads(rows || []); }).catch(() => {});
      }
    }
    try {
      const next = inThread && loaders.current.loadThreadChat
        ? await loaders.current.loadThreadChat(student.id, inThread.id, before)
        : await loaders.current.loadChat(student.id, before);
      if (want.current !== token) return;           // they moved on to someone (or some tab) else
      setPage((cur) => (before === null ? next : { messages: [...next.messages, ...cur.messages], has_more: next.has_more }));
    } catch (e) {
      if (want.current === token) setError(errorMessage(e, "Couldn't load that conversation"));
    } finally {
      if (want.current === token) setBusy(false);
    }
  };

  const back = () => {
    if (thread) { read(open, null, null); return; }   // from a tab back to the main conversation
    want.current = null; setOpen(null); setThread(null); setThreads([]); setError(""); setBusy(false);
  };

  if (!students && !error) return <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={22} /></Box>;
  const errorLine = error
    ? <Typography sx={{ px: 2, py: 1, fontFamily: FONT, fontSize: 13, color: "#b42318" }}>{error}</Typography> : null;

  if (open) {
    const oldest = page.messages.find((m) => typeof m.i === "number")?.i ?? null;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: { xs: 520, md: "100%" } }}>
        <Box sx={{ px: 1, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #eef0f2" }}>
          <IconButton size="small" onClick={back} aria-label={thread ? "Back to the main conversation" : "Back to students"}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {open.name}{thread ? ` · ${thread.title}` : ""}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#6b7280", flexShrink: 0 }}>· read only</Typography>
        </Box>
        {thread && (
          <Box data-testid="review-pinned" sx={{ px: 2, py: 0.6, borderBottom: "1px solid #eef0f2", backgroundColor: "#f8fbfd", display: "flex", gap: 0.75 }}>
            <CallSplitIcon sx={{ fontSize: 15, color: "#0172A6", mt: "2px" }} />
            <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#374151", fontStyle: "italic" }}>“{thread.quote}”</Typography>
          </Box>
        )}
        {errorLine}
        <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, pt: 1 }}>
          {!thread && threads.length > 0 && (
            <Box data-testid="review-threads" sx={{ mb: 1.25, p: 1, borderRadius: 1.5, border: "1px solid #e5e7eb", backgroundColor: "#fafbfc" }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#6b7280", mb: 0.5 }}>
                Side conversations · {threads.length}
              </Typography>
              {flatten(threads).map(({ thread: t, depth }) => (
                <Box key={t.id} role="button" tabIndex={0} onClick={() => read(open, null, t)}
                  onKeyDown={(e) => { if (e.key === "Enter") read(open, null, t); }}
                  sx={{ display: "flex", alignItems: "center", gap: 0.75, pl: depth * 1.5, py: 0.35, cursor: "pointer",
                    "&:hover": { color: "#0172A6" } }}>
                  <CallSplitIcon sx={{ fontSize: 14, color: "#0172A6" }} />
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {t.title}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: "#6b7280", flexShrink: 0 }}>
                    {t.exchanges ? `${t.exchanges} q` : "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {busy && page.messages.length === 0 && <Box sx={{ textAlign: "center", mt: 3 }}><CircularProgress size={20} /></Box>}
          {page.has_more && (
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Button size="small" disabled={busy} onClick={() => read(open, oldest, thread)} sx={{ textTransform: "none", fontSize: 12 }}>
                Load earlier messages
              </Button>
            </Box>
          )}
          {!busy && !error && page.messages.length === 0 && (
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: "#6b7280", textAlign: "center", mt: 3 }}>No questions yet.</Typography>
          )}
          {page.messages.map((m, n) => <Message key={m.i ?? n} m={m} onSource={onSource} isClickable={isClickable} />)}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: { xs: "70vh", md: "100%" }, height: { md: "100%" }, overflowY: "auto" }}>
      {errorLine}
      {(students || []).map((s) => (
        <Box key={s.id} role="button" tabIndex={0} onClick={() => read(s)}
          onKeyDown={(e) => { if (e.key === "Enter") read(s); }}
          sx={{ px: 2, py: 1.1, borderBottom: "1px solid #f3f4f6", cursor: "pointer", "&:hover": { backgroundColor: "#f8fafc" } }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, fontWeight: 500 }}>{s.name}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#6b7280" }}>
            {s.exchanges ? `${s.exchanges} question${s.exchanges === 1 ? "" : "s"} · last ${ago(s.last_at)}` : "No questions yet"}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
